import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MenuMovil } from './MenuMovil';
import { Header } from './Header';
import { useSocket } from '@/hooks/useSocket';
import { useAuth } from '@/hooks/useAuth';
import { shouldAutoStartTour, startTour } from '@/lib/tour';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWorkspaceStore } from '@/store/workspace.store';
import { BotonAthena } from '@/components/athena/BotonAthena';
import { Buscador } from '@/components/athena/Buscador';
import { DeshacerProvider } from '@/hooks/useDeshacer';
import { FranjaVerComo } from './VerComo';
import { AnfitrionDePestanas } from '@/components/pestanas/AnfitrionDePestanas';

/** Shell autenticado: sidebar + header + área de contenido con <Outlet>. */
export function Layout() {
  // NEGOCIO ACTIVO. Va PRIMERO, antes que nada que pida datos: el resto del
  // panel manda la cabecera `X-Workspace-Id` con lo que este hook deje elegido.
  // Con un solo negocio el servidor lo resuelve igual, así que una instancia de
  // cliente no nota nada de esto.
  const { cargado: workspacesCargados } = useWorkspaces();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeId);

  // Mantiene viva la conexión de tiempo real mientras hay sesión.
  useSocket();

  // Tour de bienvenida: se auto-lanza UNA vez por navegador al primer login de
  // un ADMIN (con un respiro para que el sidebar ya esté pintado). Re-lanzable
  // desde el menú de Ayuda del header.
  const { user } = useAuth();
  useEffect(() => {
    if (!shouldAutoStartTour(user?.role)) return;
    const t = setTimeout(startTour, 800);
    return () => clearTimeout(t);
  }, [user?.role]);

  return (
    <DeshacerProvider>
    <div className="flex h-full w-full overflow-hidden bg-surface-muted dark:bg-slate-950 lateral:gap-3 lateral:p-3">
      {/* La barra lateral es de ESCRITORIO. En el teléfono el mismo menú sale de la
          hamburguesa de arriba a la izquierda (`MenuMovil`), con todas las pantallas. Hasta
          el 13-oct había abajo una barra fija de accesos y la hamburguesa no hacía nada:
          el resto del menú no tenía entrada en el teléfono. */}
      <div className="hidden lateral:flex">
        <Sidebar />
      </div>
      {/* 13-oct: marco flotante de esquinas redondas en escritorio (composición de Cielo); en teléfono, de borde a borde. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden lateral:rounded-3xl lateral:border lateral:border-surface-border lateral:dark:border-slate-800">
        <FranjaVerComo />
        <Header />
        {/* Margenes iguales en todas las pantallas: 16 px en movil, 24 en
            escritorio. `pb-24` deja sitio para el boton flotante de Athena,
            que si no tapa la ultima fila de cada lista. */}
        {/* El buscador global. Va en el shell y no en cada pantalla: el atajo
            tiene que funcionar en las siete, no en cuatro.
            Pegado al borde superior y sin margen propio, para no abrir un
            hueco vacio arriba de cada pantalla. */}
        <div className="flex justify-end px-4 pt-2 md:px-6">
          <Buscador />
        </div>
        {/* Lo que se abre (un producto, una venta, un cliente, un asunto) queda como pestaña
            arriba, tipo navegador: el anfitrión pinta la barra y esconde esta pantalla, sin
            desmontarla, mientras se mira (regla del 9-oct para todo Conversia). */}
        <AnfitrionDePestanas>
        <main className="flex-1 overflow-auto px-4 py-4 pb-24 md:px-6 md:pb-6">
          {/* No se pintan las pantallas hasta saber qué negocio está activo:
              pedir datos antes daría un 403 legítimo que se vería como un
              error. Con un solo negocio esto resuelve en el primer render.

              La `key` con el id del negocio es lo que hace que D2 se cumpla de
              verdad: al cambiar de negocio, React DESMONTA y vuelve a montar
              todo el árbol, así que cada pantalla arranca de cero y vuelve a
              pedir sus datos con la cabecera nueva. Vaciar la caché no bastaba
              — se veía el negocio nuevo en el selector con los datos del
              anterior debajo, que es justo lo que este trabajo evita. */}
          {workspacesCargados ? <Outlet key={activeWorkspaceId ?? 'sin-negocio'} /> : null}
        </main>
        </AnfitrionDePestanas>
      </div>
      {workspacesCargados ? (
        <>
          <MenuMovil />
          <BotonAthena />
        </>
      ) : null}
    </div>
    </DeshacerProvider>
  );
}
